/**
 * Marketplace form helpers — UTM tracking, HubSpot submission, ChiliPiper routing.
 * Mirrors the patterns used by /request-demo and /request-cmmc-demo via useHubSpotForm.
 */

import sbjs from 'sourcebuster'
import { LINKEDIN_CLICK_ID_COOKIE, getLinkedInClickId, resolveLinkedInPaid } from '@/utils/linkedinAttribution'
import Cookies from 'js-cookie'
import { logger } from '@/utils/logger'
import type { CartItem } from '@/types/marketplace'
import { SEGMENT_CONFIG_MAP } from '@/constants/microsoftCategories'
import { formatPrice } from '@/utils/microsoftFormatters'

/** HubSpot form field (textarea) — full quote cart line items + totals */
export const HUBSPOT_MARKETPLACE_QUOTE_CART_FIELD = 'marketplace_quote_cart'

export interface UtmFields {
  current_traffic_type?: string
  utm_source__c?: string
  utm_medium__c?: string
  utm_campaign__c?: string
  utm_content__c?: string
  utm_term__c?: string
  first_traffic_type?: string
  first_utm_source?: string
  first_utm_medium?: string
  first_utm_campaign?: string
  first_utm_content?: string
  first_utm_term?: string
  current_datetime?: string
  current_entrance_point__c?: string
  current_referrer_url?: string
  first_datetime?: string
  first_entrance_point__c?: string
  first_referrer_url?: string
  current_page_url?: string
  gclid?: string
  fb_bid?: string
  oppref?: string
  linkedin_click_id?: string
}

const SOURCEBUSTER_FALLBACKS = new Set(['google_cpc', 'yandex_cpc', '(none)'])

export { initSourcebuster } from '@/utils/sourcebusterConfig'

/** Resolve a UTM value, preferring URL > sourcebuster current > sourcebuster first */
function resolveUtm(
  currentVal: string | undefined,
  firstVal: string | undefined,
  urlParam: string,
): string | undefined {
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  const fromUrl = urlParams.get(urlParam)
  if (fromUrl) return fromUrl
  if (currentVal && !SOURCEBUSTER_FALLBACKS.has(currentVal)) return currentVal
  if (firstVal && !SOURCEBUSTER_FALLBACKS.has(firstVal)) return firstVal
  return currentVal
}

/** Get all UTM fields from sourcebuster cookies, ready for HubSpot submission */
export function getUtmFields(): UtmFields {
  if (typeof window === 'undefined') return {}

  const current = sbjs?.get?.current
  const first = sbjs?.get?.first
  const linkedin = resolveLinkedInPaid({
    source: current?.src,
    medium: current?.mdm,
    campaign: current?.cmp,
    entrancePoint: sbjs?.get?.current_add?.ep,
    search: window.location.search,
  })

  const fields: UtmFields = {
    current_traffic_type: current?.typ,
    utm_source__c: linkedin?.utm_source__c ?? current?.src,
    utm_medium__c: linkedin?.utm_medium__c ?? current?.mdm,
    utm_campaign__c: resolveUtm(current?.cmp, first?.cmp, 'utm_campaign'),
    utm_content__c: resolveUtm(current?.cnt, first?.cnt, 'utm_content'),
    utm_term__c: resolveUtm(current?.trm, first?.trm, 'utm_term'),
    first_traffic_type: first?.typ,
    first_utm_source: first?.src,
    first_utm_medium: first?.mdm,
    first_utm_campaign: first?.cmp,
    first_utm_content: first?.cnt,
    first_utm_term: first?.trm,
    current_datetime: sbjs?.get?.current_add?.fd,
    current_entrance_point__c: sbjs?.get?.current_add?.ep,
    current_referrer_url: sbjs?.get?.current_add?.rf,
    first_datetime: sbjs?.get?.first_add?.fd,
    first_entrance_point__c: sbjs?.get?.first_add?.ep,
    first_referrer_url: sbjs?.get?.first_add?.rf,
    current_page_url: typeof window !== 'undefined' ? window.location.href : undefined,
  }

  const gclid = Cookies.get('gclid')
  if (gclid) fields.gclid = gclid

  const fbp = Cookies.get('_fbp')
  if (fbp) fields.fb_bid = fbp

  // OpenAI / ChatGPT Ads click ID (oppref) — `oppref` from GclidTracker, `__oppref`
  // from the OpenAI pixel SDK as a fallback. Used for offline conversion attribution.
  const oppref = Cookies.get('oppref') || Cookies.get('__oppref')
  if (oppref) fields.oppref = oppref

  const linkedInClickId = getLinkedInClickId(window.location.search, Cookies.get(LINKEDIN_CLICK_ID_COOKIE))
  if (linkedInClickId) fields.linkedin_click_id = linkedInClickId

  return fields
}

/** Carts saved before lines carried a part number, and segments without one, keep the slug-derived SKU */
export function cartItemSkuDisplay(item: CartItem): string {
  return item.msPartNumber || item.productSlug.toUpperCase().replace(/-/g, '_')
}

export interface MarketplaceQuoteCartTotals {
  monthly: number
  annual: number
}

/**
 * Serialize quote cart for HubSpot textarea — human-readable line items and totals.
 * Includes every cart value shown on /marketplace/quote (SKU, segment, qty, MSRP, line totals).
 */
export function buildMarketplaceQuoteCartHubSpotText(
  items: CartItem[],
  totals: MarketplaceQuoteCartTotals,
): string {
  const lines: string[] = ['=== Microsoft Marketplace Quote Cart ===', '']

  for (const item of items) {
    const segLabel = SEGMENT_CONFIG_MAP[item.segment]?.label ?? item.segment
    const sku = cartItemSkuDisplay(item)

    lines.push(`Product: ${item.productTitle}`)
    lines.push(`Vendor: ${item.vendor === 'google-workspace' ? 'Google Workspace' : 'Microsoft'}`)
    lines.push(`SKU: ${sku}`)
    if (item.variantSlug) lines.push(`Variant: ${item.variantSlug}`)
    lines.push(`Segment: ${segLabel} (${item.segment})`)
    lines.push(`Quantity: ${item.quantity}`)
    lines.push(`MSRP/License (monthly): ${formatPrice(item.erpMonthly)}`)
    lines.push(`MSRP/License (annual): ${formatPrice(item.erpAnnual)}`)
    lines.push(`Your price/License (monthly): ${formatPrice(item.erpMonthly)}`)
    lines.push(`Line monthly: ${formatPrice(item.erpMonthly * item.quantity)}`)
    lines.push(`Line annual: ${formatPrice(item.erpAnnual * item.quantity)}`)
    lines.push('---')
  }

  const totalLicenses = items.reduce((sum, item) => sum + item.quantity, 0)
  lines.push('')
  lines.push(`Total licenses: ${totalLicenses}`)
  lines.push(`Estimated monthly total: ${formatPrice(totals.monthly)}`)
  lines.push(`Estimated annual total: ${formatPrice(totals.annual)}`)

  return lines.join('\n')
}

/** Build HubSpot form fields array from a values object */
export function toHubSpotFields(
  values: Record<string, string | number | undefined>,
): Array<{ name: string; value: string }> {
  return Object.entries(values)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([name, value]) => ({ name, value: String(value) }))
}

/** Submit a form to HubSpot Forms API with UTM enrichment */
export async function submitHubSpotForm(
  portalId: string,
  formId: string,
  values: Record<string, string | number | undefined>,
  context: { pageName?: string; pageUri?: string } = {},
): Promise<{ success: boolean; status: number }> {
  const utmFields = getUtmFields()
  const allFields = toHubSpotFields({ ...values, ...utmFields })

  try {
    const res = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fields: allFields,
        context: {
          pageUri: context.pageUri ?? (typeof window !== 'undefined' ? window.location.href : ''),
          pageName: context.pageName ?? 'Marketplace Quote Request',
          hutk: typeof document !== 'undefined' ? Cookies.get('hubspotutk') : undefined,
        },
      }),
    })

    if (!res.ok) {
      const body = await res.text().catch(() => '')
      logger.error('[HubSpot] form submission failed', { status: res.status, formId, body: body.slice(0, 500) })
    }

    return { success: res.ok, status: res.status }
  } catch (err) {
    logger.error('[HubSpot] form submission error', {
      error: err instanceof Error ? err.message : String(err),
      formId,
    })
    return { success: false, status: 0 }
  }
}

// ---------------------------------------------------------------------------
// ChiliPiper integration (Window.ChiliPiper typed in global.d.ts)
// ---------------------------------------------------------------------------

let chilipiperLoadingPromise: Promise<void> | null = null

/** Load the ChiliPiper concierge script (idempotent) */
export function loadChiliPiper(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.ChiliPiper) return Promise.resolve()
  if (chilipiperLoadingPromise) return chilipiperLoadingPromise

  chilipiperLoadingPromise = new Promise((resolve, reject) => {
    const scriptId = 'chilipiper-concierge-script'
    let script = document.getElementById(scriptId) as HTMLScriptElement | null

    if (!script) {
      script = document.createElement('script')
      script.id = scriptId
      script.src = 'https://secureframe.chilipiper.com/concierge-js/cjs/concierge.js'
      script.async = true
      document.body.appendChild(script)
    }

    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener(
      'error',
      (e) => {
        logger.error('[ChiliPiper] script load failed', { error: e instanceof Error ? e.message : String(e) })
        chilipiperLoadingPromise = null
        reject(e)
      },
      { once: true },
    )
  })

  return chilipiperLoadingPromise
}

/** Submit lead info to ChiliPiper for routing (used after HubSpot submission for gov segments) */
export async function submitToChiliPiper(
  formType: string,
  lead: { firstname: string; lastname: string; email: string; company: string; phone?: string },
): Promise<void> {
  try {
    await loadChiliPiper()
    if (typeof window === 'undefined' || !window.ChiliPiper) return

    window.ChiliPiper.submit('secureframe', formType, {
      map: true,
      lead: {
        FirstName: lead.firstname,
        LastName: lead.lastname,
        Email: lead.email,
        Company: lead.company,
        Phone: lead.phone ?? '',
      },
    })
  } catch (err) {
    logger.error('[ChiliPiper] submit failed', { error: err instanceof Error ? err.message : String(err), formType })
  }
}

/** Determine if cart contains a segment that requires ChiliPiper routing (gov/classified) */
export function needsChiliPiper(segments: string[]): boolean {
  return segments.some((s) => ['gcc', 'gcc-high', 'secret', 'top-secret'].includes(s))
}
